import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminUsersService, AdminUserSummary } from '../services/adminUsers';
import { authService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const roleOptions = [
  { value: '', label: 'All Roles' },
  { value: 'student', label: 'Students' },
  { value: 'professor', label: 'Professors' },
];

const UsersManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user, impersonate } = useAuth();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sendingResetId, setSendingResetId] = useState<number | null>(null);
  const [sentResetIds, setSentResetIds] = useState<Set<number>>(new Set());
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetTargetId, setResetTargetId] = useState<number | null>(null);
  const [resetTargetEmail, setResetTargetEmail] = useState('');
  const [resetTargetName, setResetTargetName] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetExpiresMinutes, setResetExpiresMinutes] = useState('30');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ pages: 1, has_next: false, has_prev: false, per_page: 20, total: 0, page: 1 });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminUsersService.list({
        page: currentPage,
        per_page: pagination.per_page,
        role: roleFilter || undefined,
        search: search || undefined,
      });
      setUsers(response.users || []);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pagination.per_page, roleFilter, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleImpersonate = async (target: AdminUserSummary) => {
    if (target.is_admin) {
      setError('Cannot impersonate admin users.');
      return;
    }
    if (user?.id === target.id) {
      setError('Cannot impersonate yourself.');
      return;
    }
    setNotice('');
    setError('');
    const result = await impersonate(target.id);
    if (!result.success || !result.user) {
      setError('Failed to impersonate user');
      return;
    }
    if (result.user.role === 'professor') {
      navigate('/professor-pathway');
    } else {
      navigate('/student-pathway');
    }
  };

  const handleSendReset = async (id: number, email: string, message?: string, expiresMinutes?: number) => {
    if (!email) {
      setError('No email available for this user.');
      return;
    }
    setError('');
    setNotice('');
    try {
      setSendingResetId(id);
      await authService.requestAdminPasswordReset({
        email,
        message,
        expires_minutes: expiresMinutes,
      });
      setNotice(`Password reset link sent to ${email}.`);
      setSentResetIds((prev) => new Set(prev).add(id));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to send reset link.');
    } finally {
      setSendingResetId(null);
    }
  };

  const openResetModal = (id: number, email?: string, name?: string) => {
    if (!email) {
      setError('No email available for this user.');
      return;
    }
    setError('');
    setNotice('');
    setResetTargetId(id);
    setResetTargetEmail(email);
    setResetTargetName(name || '');
    setResetMessage('');
    setResetExpiresMinutes('30');
    setResetModalOpen(true);
  };

  const closeResetModal = () => {
    setResetModalOpen(false);
    setResetTargetId(null);
    setResetTargetEmail('');
    setResetTargetName('');
  };

  const submitResetModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetTargetId || !resetTargetEmail) return;
    const minutes = Number(resetExpiresMinutes);
    const expiresMinutes = Number.isNaN(minutes) ? 30 : minutes;
    await handleSendReset(resetTargetId, resetTargetEmail, resetMessage, expiresMinutes);
    closeResetModal();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="bg-gray-200 h-64 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-2 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">User Management</h2>
          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">View users by role and impersonate as needed</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Search users</h3>
          </div>
          <form onSubmit={handleSearch} className="flex space-x-3 p-4">
            <input
              type="text"
              placeholder="Search by name, username, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
              aria-label="Search users"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </button>
          </form>
        </div>
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Filter by role</h3>
          </div>
          <div className="flex flex-wrap gap-2 p-4">
            {roleOptions.map((role) => (
              <button
                key={role.value}
                onClick={() => {
                  setRoleFilter(role.value);
                  setCurrentPage(1);
                }}
                className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors px-4 py-2 text-sm ${
                  roleFilter === role.value
                    ? 'bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:border-blue-700'
                    : 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                {role.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-md mb-6">
          {notice}
        </div>
      )}

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Users</h3>
          <p className="text-xs text-gray-500">{pagination.total} total</p>
        </div>
        {users.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 border-b border-gray-200 dark:border-slate-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Username</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Role</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Created</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {users.map((u) => {
                    const name = u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '—';
                    return (
                      <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/60 transition duration-150">
                        <td className="px-6 py-4 text-gray-700">{name}</td>
                        <td className="px-6 py-4 text-gray-700">{u.username || '—'}</td>
                        <td className="px-6 py-4 text-gray-700">{u.email || '—'}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800 dark:bg-slate-700 dark:text-slate-100">
                            {u.is_admin ? 'admin' : u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleImpersonate(u)}
                            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-amber-500 text-amber-950 border border-amber-500 hover:bg-amber-600 hover:border-amber-600 px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title="View as user"
                            disabled={u.is_admin || user?.id === u.id}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A10 10 0 1118.88 6.195M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => openResetModal(
                              u.id,
                              u.email,
                              `${u.first_name || ''} ${u.last_name || ''}`.trim()
                            )}
                            className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors px-3 py-1 text-sm ml-2 ${
                              sentResetIds.has(u.id)
                                ? 'bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700'
                              : 'bg-slate-900 text-white border border-slate-900 hover:bg-slate-800 hover:border-slate-800'
                            }`}
                            title={sentResetIds.has(u.id) ? 'Reset link sent' : 'Send password reset link'}
                            aria-label="Send password reset link"
                            disabled={sendingResetId === u.id}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10V6a2 2 0 10-4 0v4m8 0V8a4 4 0 00-8 0v2m8 0h1a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2v-6a2 2 0 012-2h1m4 4v2" />
                            </svg>
                            {sendingResetId === u.id ? 'Sending…' : sentResetIds.has(u.id) ? 'Sent' : 'Send'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="bg-white/60 px-6 py-4 border-t border-gray-200">
                <nav className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!pagination.has_prev}
                      className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:border-blue-700 px-3 py-2 text-sm">
                      {pagination.page}
                    </span>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!pagination.has_next}
                      className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600 mb-6">
              {search || roleFilter
                ? 'No users match your current filters. Try adjusting your search criteria.'
                : 'No users available yet.'}
            </p>
          </div>
        )}
      </div>

      {resetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/60" onClick={closeResetModal} />
          <div className="relative z-10 w-full max-w-lg bg-white/90 dark:bg-slate-900/80 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_20px_48px_rgba(15,23,42,0.24)] dark:shadow-[0_24px_56px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Send reset link</h3>
                <p className="text-xs text-slate-500 dark:text-slate-300">
                  {resetTargetName ? `${resetTargetName} • ` : ''}{resetTargetEmail}
                </p>
              </div>
              <button onClick={closeResetModal} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100" aria-label="Close">
                ✕
              </button>
            </div>
            <form onSubmit={submitResetModal} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Custom message (optional)</label>
                <textarea
                  value={resetMessage}
                  onChange={(e) => setResetMessage(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                  placeholder="Add context so the recipient knows this is legit."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Link expiry (minutes)</label>
                <input
                  type="number"
                  min={5}
                  max={1440}
                  value={resetExpiresMinutes}
                  onChange={(e) => setResetExpiresMinutes(e.target.value)}
                  className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Min 5, max 1440 (24 hours).</p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeResetModal}
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
                >
                  Send reset link
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
